/**
 * ColorBankService — Banque hebdomadaire de recettes couleur par cat/sous-cat
 * ────────────────────────────────────────────────────────────────────────────
 *
 * La banque est générée 1×/semaine en backend (Lambda outfit-color-bank-cron).
 * Chaque entrée = 1 recette couleur pour une (catégorie, sous-catégorie) :
 *   { primary_color, color_detail, secondary_colors, color_tone,
 *     works_with, avoid_with, formality_min, formality_max,
 *     best_seasons, best_occasions, versatility, description }
 *
 * Consommée par scoreColorHarmony (OutfitEngine) et scoreItemAgainstPiece
 * (StyleBankService) pour pondérer le matching couleur par cat/sous-cat.
 *
 * Usage :
 *   import { getColorBank, getRecipesFor, scoreItemColor } from './ColorBankService';
 *   const bank = await getColorBank();
 *   const recipes = getRecipesFor(bank, 'Hauts', 'jean slim');
 *   const score = scoreItemColor(item, bank); // 0-1, qualité couleur de l'item
 */
import AsyncStorage from '@react-native-async-storage/async-storage';
import { AWS_CONFIG } from '../config/aws_config';
import { getAuthToken } from './authHelpers';

const API_URL = AWS_CONFIG.api?.baseUrl || AWS_CONFIG.apiUrl || '';
const CACHE_KEY = 'color_bank_v1';
const CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 jours (= rythme du cron)

/**
 * Récupère la banque (cache-first, fallback API, fallback cache stale).
 * @returns {Promise<{ recipes: Array, byKey: Object, week_of: string|null, fromCache: boolean }>}
 */
export async function getColorBank({ forceRefresh = false } = {}) {
  // 1) Cache
  if (!forceRefresh) {
    try {
      const cached = await AsyncStorage.getItem(CACHE_KEY);
      if (cached) {
        const parsed = JSON.parse(cached);
        const age = Date.now() - (parsed._cachedAt || 0);
        if (age < CACHE_TTL_MS && Array.isArray(parsed.recipes)) {
          console.log(`[ColorBank] 💾 cache hit (${parsed.recipes.length} recipes, age ${Math.round(age / 1000 / 3600)}h)`);
          return { ...parsed, byKey: buildByKey(parsed.recipes), fromCache: true };
        }
      }
    } catch (e) {
      console.warn('[ColorBank] cache read error:', e.message);
    }
  }

  // 2) Fetch API
  try {
    const url = `${API_URL}/color-bank`;
    const token = await getAuthToken().catch(() => null);
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    console.log('[ColorBank] 🌐 fetching', url, token ? '(with auth)' : '(no auth)');
    const res = await fetch(url, { method: 'GET', headers });
    if (!res.ok) throw new Error(`API ${res.status}`);
    const data = await res.json();

    const toCache = { recipes: data.recipes || [], week_of: data.week_of, total: data.total, _cachedAt: Date.now() };
    try { await AsyncStorage.setItem(CACHE_KEY, JSON.stringify(toCache)); } catch {}

    console.log(`[ColorBank] ✓ fetched ${data.recipes?.length || 0} recipes (week=${data.week_of})`);
    return { ...toCache, byKey: buildByKey(toCache.recipes), fromCache: false };
  } catch (e) {
    console.warn('[ColorBank] fetch error:', e.message);
    // Fallback cache stale
    try {
      const cached = await AsyncStorage.getItem(CACHE_KEY);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed.recipes)) {
          console.log('[ColorBank] ↩️ stale cache fallback');
          return { ...parsed, byKey: buildByKey(parsed.recipes), fromCache: true, stale: true };
        }
      }
    } catch {}
    return { recipes: [], byKey: {}, week_of: null, fromCache: false, error: e.message };
  }
}

// ─── Helper : index recipes par "category:subcategory" pour lookup O(1) ───
function buildByKey(recipes) {
  const map = {};
  for (const r of recipes) {
    const key = `${r.category}:${(r.subcategory || '').toLowerCase()}`;
    if (!map[key]) map[key] = [];
    map[key].push(r);
  }
  return map;
}

/**
 * Récupère les recettes pour une (category, subcategory).
 * Si subcategory exact pas trouvé, fallback sur fuzzy match (substring).
 * @param {Object} bank Résultat de getColorBank()
 * @param {string} category Catégorie de l'item (ex: "Hauts")
 * @param {string} subcategory Sous-cat (ex: "jean slim", "pull col rond")
 * @returns {Array} Recettes correspondantes (peut être vide)
 */
export function getRecipesFor(bank, category, subcategory) {
  if (!bank?.byKey || !category) return [];
  const subLower = (subcategory || '').toLowerCase().trim();
  // Match exact
  const exactKey = `${category}:${subLower}`;
  if (bank.byKey[exactKey]?.length > 0) return bank.byKey[exactKey];
  // Fuzzy : chercher une subcategory qui contient les mots-clés ou inversement
  const candidates = [];
  for (const key of Object.keys(bank.byKey)) {
    if (!key.startsWith(`${category}:`)) continue;
    const keySub = key.split(':')[1] || '';
    if (subLower && (keySub.includes(subLower) || subLower.includes(keySub))) {
      candidates.push(...bank.byKey[key]);
    }
  }
  return candidates;
}

/**
 * Score de qualité couleur d'un item par rapport au color bank (0-1).
 * Compare la couleur de l'item (primaryColor + colorDetail + colorTone) aux
 * recettes recommandées pour sa (category, subcategory). Retourne :
 *   - 1.0 si match exact (même primaryColor + même colorDetail + même tone)
 *   - 0.85 si même primaryColor + même tone (mais détail différent)
 *   - 0.7 si même primaryColor (tone différent)
 *   - 0.5 si pas de match mais couleur acceptable (présente comme secondary)
 *   - 0.3 si couleur dans avoid_with (à éviter)
 *   - 0.6 par défaut (pas d'info, neutre)
 *
 * @param {Object} item Item du dressing/catalogue (avec ai_metadata)
 * @param {Object} bank Color bank
 * @returns {{ score: number, matchedRecipe: Object|null, reason: string }}
 */
export function scoreItemColor(item, bank) {
  if (!bank?.byKey || !item?.ai_metadata) return { score: 0.6, matchedRecipe: null, reason: 'no-data' };
  const ai = item.ai_metadata;
  const subcategory = ai.subcategory || ai.detectedType;
  const category = item.category;
  if (!category || !subcategory) return { score: 0.6, matchedRecipe: null, reason: 'no-subcat' };

  const recipes = getRecipesFor(bank, category, subcategory);
  if (recipes.length === 0) return { score: 0.6, matchedRecipe: null, reason: 'no-recipes' };

  const itemPrimary = (ai.primaryColor || '').toLowerCase();
  const itemDetail = (ai.colorDetail || '').toLowerCase();
  const itemTone = (ai.colorTone || '').toLowerCase();

  // Recherche du meilleur match parmi les recettes
  let best = { score: 0.5, recipe: null, reason: 'no-match' };
  for (const r of recipes) {
    const rPrimary = (r.primary_color || '').toLowerCase();
    const rDetail = (r.color_detail || '').toLowerCase();
    const rTone = (r.color_tone || '').toLowerCase();
    const rSecondaries = (r.secondary_colors || []).map(c => (c || '').toLowerCase());

    // Match exact
    if (itemPrimary === rPrimary && itemDetail && itemDetail === rDetail) {
      return { score: 1.0, matchedRecipe: r, reason: 'exact-primary+detail' };
    }
    // Same primary + same tone
    if (itemPrimary === rPrimary && itemTone && itemTone === rTone) {
      if (best.score < 0.85) best = { score: 0.85, recipe: r, reason: 'primary+tone' };
    }
    // Same primary
    else if (itemPrimary === rPrimary) {
      if (best.score < 0.7) best = { score: 0.7, recipe: r, reason: 'primary-only' };
    }
    // Item primary listé comme secondary dans la recette → couleur acceptable
    else if (rSecondaries.includes(itemPrimary)) {
      if (best.score < 0.6) best = { score: 0.6, recipe: r, reason: 'as-secondary' };
    }
  }

  return { score: best.score, matchedRecipe: best.recipe, reason: best.reason };
}
